import type { GatewayBrowserClient } from "../gateway.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import {
  buildFileChangesInjectBody,
  FILE_CHANGES_FLEET_LABEL,
  FILE_CHANGES_LABEL,
  parseFileChangesPayload,
  type SessionArtifactSummary,
} from "./chat-file-changes-summary.ts";
import { getChatRunArtifactsSinceTs } from "./chat-run-artifacts.ts";
import {
  isChatExecutionIdle,
  shouldSkipFleetCompletionForOrchestration,
} from "./fleet-completion-notice.ts";
import {
  buildArtifactSummaryFromChangedPaths,
  collectChangedPathsFromCards,
  enrichCardsWithJobChangedPaths,
  fetchSelfImprovementJobs,
  indexSelfImprovementJobsByIssue,
  indexSelfImprovementJobsByWorkerSession,
  mergeArtifactSummaries,
} from "./fleet-subagent-changed-paths.ts";
import { resolveAgentIdForSession } from "./resolve-session-agent.ts";
import { filterSubagentRunCards } from "./subagent-run-display.ts";
import { summarizeSubagentFleet } from "./subagent-run-status.ts";

export type FileChangesNoticeHost = {
  sessionKey: string;
  chatRunId: string | null;
  chatSending: boolean;
  client: GatewayBrowserClient | null;
  connected?: boolean;
  chatRunArtifactsSinceTs: Record<string, number>;
  fileChangesNotifiedFingerprint: Record<string, string>;
  fleetFileChangesNotifiedFingerprint: Record<string, string>;
  chatSubagents?: ChatSubagentCard[];
  chatMessages?: unknown[];
};

export async function fetchArtifactSummaryForPending(
  host: FileChangesNoticeHost,
): Promise<SessionArtifactSummary | null> {
  if (!host.client || !host.connected) {
    return null;
  }
  const sessionKey = host.sessionKey.trim();
  if (!sessionKey) {
    return null;
  }
  const agentId = resolveAgentIdForSession(sessionKey);
  const sinceTs = getChatRunArtifactsSinceTs(host.chatRunArtifactsSinceTs, sessionKey);
  return fetchArtifactSummary(host.client, {
    agentId,
    sessionKey,
    sinceTs,
  });
}

async function fetchArtifactSummary(
  client: GatewayBrowserClient,
  params: {
    agentId: string;
    sessionKey?: string;
    sessionKeys?: string[];
    sinceTs?: number;
  },
): Promise<SessionArtifactSummary | null> {
  try {
    return await client.request<SessionArtifactSummary>("sessions.artifacts.summary", {
      agentId: params.agentId,
      ...(params.sessionKeys?.length
        ? { sessionKeys: params.sessionKeys }
        : { sessionKey: params.sessionKey }),
      ...(params.sinceTs != null ? { sinceTs: params.sinceTs } : {}),
    });
  } catch {
    return null;
  }
}

function summaryFingerprint(sessionKey: string, summary: SessionArtifactSummary): string {
  const files = summary.files.map((f) => `${f.path}:${f.op}:${f.additions}:${f.deletions}`).sort();
  return `${sessionKey}|${files.join("|")}`;
}

function hasFingerprintInTranscript(
  messages: unknown[] | undefined,
  sessionKey: string,
  fingerprint: string,
): boolean {
  if (!messages?.length) {
    return false;
  }
  const expected = fingerprint;
  for (let i = messages.length - 1; i >= 0 && i >= messages.length - 50; i -= 1) {
    const payload = parseFileChangesPayload(messages[i]);
    if (!payload?.summary) {
      continue;
    }
    if (summaryFingerprint(sessionKey, payload.summary) === expected) {
      return true;
    }
  }
  return false;
}

export function evaluateFileChangesNotice(host: FileChangesNoticeHost): {
  shouldInject: boolean;
  fingerprint?: string;
  message?: string;
  label?: string;
} {
  if (!host.connected || !host.client) {
    return { shouldInject: false };
  }
  if (!isChatExecutionIdle(host)) {
    return { shouldInject: false };
  }
  const sessionKey = host.sessionKey.trim();
  if (!sessionKey) {
    return { shouldInject: false };
  }
  return { shouldInject: true, label: FILE_CHANGES_LABEL };
}

export async function resolveFileChangesNotice(
  host: FileChangesNoticeHost,
): Promise<{ shouldInject: boolean; fingerprint?: string; message?: string; label?: string }> {
  const base = evaluateFileChangesNotice(host);
  if (!base.shouldInject || !host.client) {
    return { shouldInject: false };
  }
  const sessionKey = host.sessionKey.trim();
  const agentId = resolveAgentIdForSession(sessionKey);
  const sinceTs = getChatRunArtifactsSinceTs(host.chatRunArtifactsSinceTs, sessionKey);
  const summary = await fetchArtifactSummary(host.client, {
    agentId,
    sessionKey,
    sinceTs,
  });
  if (!summary || summary.totals.files === 0) {
    return { shouldInject: false };
  }
  const fingerprint = summaryFingerprint(sessionKey, summary);
  if (host.fileChangesNotifiedFingerprint[sessionKey] === fingerprint) {
    return { shouldInject: false };
  }
  if (hasFingerprintInTranscript(host.chatMessages, sessionKey, fingerprint)) {
    return { shouldInject: false };
  }
  return {
    shouldInject: true,
    fingerprint,
    label: FILE_CHANGES_LABEL,
    message: buildFileChangesInjectBody(summary, {
      title: `${summary.totals.files} file${summary.totals.files === 1 ? "" : "s"}`,
    }),
  };
}

export function evaluateFleetFileChangesRollup(host: FileChangesNoticeHost): {
  shouldInject: boolean;
  label?: string;
} {
  if (!host.connected || !host.client) {
    return { shouldInject: false };
  }
  if (!isChatExecutionIdle(host)) {
    return { shouldInject: false };
  }
  const cards = filterSubagentRunCards(host.chatSubagents ?? []);
  if (cards.length === 0) {
    return { shouldInject: false };
  }
  const summary = summarizeSubagentFleet(cards);
  if (summary.overall === "in_progress" || summary.overall === "empty") {
    return { shouldInject: false };
  }
  if (shouldSkipFleetCompletionForOrchestration(host.chatSubagents ?? [], {})) {
    return { shouldInject: false };
  }
  return { shouldInject: true, label: FILE_CHANGES_FLEET_LABEL };
}

export async function resolveFleetFileChangesRollup(
  host: FileChangesNoticeHost,
): Promise<{ shouldInject: boolean; fingerprint?: string; message?: string; label?: string }> {
  const base = evaluateFleetFileChangesRollup(host);
  if (!base.shouldInject || !host.client) {
    return { shouldInject: false };
  }
  const sessionKey = host.sessionKey.trim();
  const runCards = filterSubagentRunCards(host.chatSubagents ?? []);
  const sessionKeys = [
    sessionKey,
    ...runCards.map((c) => c.childSessionKey?.trim()).filter((k): k is string => Boolean(k)),
  ];
  const uniqueKeys = [...new Set(sessionKeys)];
  const earliestSubagent = runCards.reduce(
    (min, c) => Math.min(min, c.createdAt ?? Number.MAX_SAFE_INTEGER),
    Number.MAX_SAFE_INTEGER,
  );
  const parentSince = getChatRunArtifactsSinceTs(host.chatRunArtifactsSinceTs, sessionKey);
  const sinceTs =
    parentSince != null && earliestSubagent !== Number.MAX_SAFE_INTEGER
      ? Math.min(parentSince, earliestSubagent)
      : (parentSince ??
        (earliestSubagent !== Number.MAX_SAFE_INTEGER ? earliestSubagent : undefined));

  const agentId = resolveAgentIdForSession(sessionKey);
  const artifactSummary = await fetchArtifactSummary(host.client, {
    agentId,
    sessionKeys: uniqueKeys,
    sinceTs,
  });

  const jobs = await fetchSelfImprovementJobs(host.client, agentId);
  const jobsBySession = indexSelfImprovementJobsByWorkerSession(jobs);
  const jobsByIssue = indexSelfImprovementJobsByIssue(jobs);
  const enrichedCards = enrichCardsWithJobChangedPaths(runCards, jobsBySession, jobsByIssue);
  const pathFallback = buildArtifactSummaryFromChangedPaths(
    collectChangedPathsFromCards(enrichedCards),
    artifactSummary?.workspace ??
      jobs.find((j) => j.repoWorkspaceDir?.trim())?.repoWorkspaceDir ??
      "",
  );
  const summary = mergeArtifactSummaries(artifactSummary, pathFallback);

  if (!summary || summary.totals.files === 0) {
    return { shouldInject: false };
  }
  const fingerprint = summaryFingerprint(`fleet:${sessionKey}`, summary);
  if (host.fleetFileChangesNotifiedFingerprint[sessionKey] === fingerprint) {
    return { shouldInject: false };
  }
  if (hasFingerprintInTranscript(host.chatMessages, `fleet:${sessionKey}`, fingerprint)) {
    return { shouldInject: false };
  }
  return {
    shouldInject: true,
    fingerprint,
    label: FILE_CHANGES_FLEET_LABEL,
    message: buildFileChangesInjectBody(summary, {
      title: `${summary.totals.files} file${summary.totals.files === 1 ? "" : "s"}`,
    }),
  };
}
